import { Signal } from '@verlo/shared/components/ui/signal';
import { Switch } from '@verlo/shared/components/ui/switch';
import { canonical, clash, describe, dishWords, listWords, unread } from '@verlo/shared/lib/allergens';
import { Notice } from '@verlo/shared/components/ui/notice';
import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { ScrollArea } from '@verlo/shared/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@verlo/shared/components/ui/tabs';
import { GripVertical, Settings, Check, X } from 'lucide-react';
import { mergeTabOrder, shouldMigrate } from '../../lib/padOrder';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { menuSectionsApi, padApi } from '@verlo/shared/services/api';
import { byMenuOrder, onTheTill, placedPositions } from '../../lib/menuSections';
import { toast } from 'sonner';

// Which tabs exist, and which dishes sit under each — lib/menuTabs.js, where they are tested.
import { KIDS_SUBS as kidsSubCategories, tabsFor, inTab, drinkSubsOf, barTabsFor, inBarTab, OTHER } from '../../lib/menuTabs';

// Category display names
const categoryDisplayNames = {
  all: 'All items',
  starters: 'Starters',
  mains: 'Mains',
  sides: 'Sides',
  desserts: 'Desserts',
  drinks: 'Drinks',
  shakes: 'Shakes',
  kids: 'Kids',
  lagers: 'Lagers',
  wines: 'Wines',
  cocktails: 'Cocktails',
  soft_drinks: 'Soft drinks',
  kids_starters: 'Starters',
  kids_mains: 'Mains',
  kids_drinks: 'Drinks',
  kids_desserts: 'Desserts',
};

// Fallback label for a category with no entry in `categoryDisplayNames` — Title Case,
// underscores to spaces — so a stray `food` category never shows up lowercase.
// A section's heading in All items. The kids' sections read "Mains", "Drinks" under the Kids
// tab, where that is enough; in All items they would look like the grown-ups' sections.
const groupLabel = (cat) => (String(cat).startsWith('kids_') ? `Kids ${prettyCategory(cat).toLowerCase()}` : prettyCategory(cat));

const prettyCategory = (cat) =>
  categoryDisplayNames[cat] ||
  String(cat).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const NO_ALLERGENS = [];

const MenuSection = ({
  menuItems: allMenuItems,
  onItemClick,
  onItemInfo,
  formatCurrency,
  showEditMode = true,
  defaultCategory = 'all',
  bar = false,   // the Bar: one row of drinks tabs (menuTabs.barTabsFor)
  // The table's allergens (canonical keys). A dish that contains one is marked in claret;
  // every other dish names its allergens quietly, so red means "check" and nothing else.
  tableAllergens = NO_ALLERGENS,
  // `fill`: the page gives the menu a fixed height (a till's screen, lg and up) and the grid
  // takes whatever the tabs leave, so only the dishes scroll. Below lg it keeps its own height.
  fill = false,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  // The operator's sections, from the Cloud by way of this node's down-sync. Read once per
  // mount; a till with no answer falls back to the diner's order and shows everything.
  const [sections, setSections] = useState(null);
  useEffect(() => {
    let live = true;
    menuSectionsApi.list()
      .then((r) => { if (live) setSections(r.data || []); })
      .catch(() => { if (live) setSections([]); });
    return () => { live = false; };
  }, []);
  const sectionPos = useMemo(() => placedPositions(sections), [sections]);
  const menuItems = useMemo(() => onTheTill(allMenuItems, sections), [allMenuItems, sections]);
  const [selectedDrinkSubCategory, setSelectedDrinkSubCategory] = useState(null);
  const [selectedKidsSubCategory, setSelectedKidsSubCategory] = useState(null);
  const menuScrollRef = useRef(null);
  const longPressTimer = useRef(null);
  
  // Edit mode and custom order state
  const [editMode, setEditMode] = useState(false);
  const [customOrder, setCustomOrder] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [orderedItems, setOrderedItems] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // A person's dish order is kept against them on the venue's till server (Oct 2026 sweep),
  // so it follows them from Till 1 to Till 2. It used to live in this browser only. An order
  // still sitting in this browser from before is moved across the first time it's found.
  // (`user_settings`, the back office's, is cloud-only and never reaches the Pi.)
  const { user } = useAuth();
  const orderKey = `verlo.menuOrder.${user?.id || 'anon'}`;
  const toMap = (ids) => Object.fromEntries(ids.map((id, i) => [id, i]));
  const savedOrder = useRef({});   // what Cancel puts back
  const keepOrder = (map) => { savedOrder.current = map; setCustomOrder(map); };
  useEffect(() => {
    if (!user?.id) return undefined;
    let live = true;
    padApi.getMenuOrder().then((r) => {
      if (!live) return;
      const ids = r.data?.item_ids || [];
      if (ids.length) { keepOrder(toMap(ids)); return; }
      let local = null;
      try { local = JSON.parse(localStorage.getItem(orderKey) || 'null'); } catch { /* storage unavailable */ }
      // Only for someone who has never saved here: after a Reset, this browser's old copy
      // is dropped rather than bringing the old order back.
      if (!shouldMigrate(r.data, local)) { try { localStorage.removeItem(orderKey); } catch { /* fine */ } return; }
      keepOrder(toMap(local));
      padApi.saveMenuOrder(local).then(() => { try { localStorage.removeItem(orderKey); } catch { /* fine */ } }).catch(() => {});
    }).catch(() => { /* offline from the till server: the menu's own order */ });
    return () => { live = false; };
  }, [orderKey, user?.id]);

  // Long press handlers for item detail. Uses Pointer Events for unified
  // mouse / touch / pen support across desktop, MacBook trackpad, iPad,
  // and kiosk-mode EPOS browsers. Also tolerates small finger movement.
  const longPressOriginRef = useRef({ x: 0, y: 0 });

  const handleLongPressStart = useCallback((item, e) => {
    if (editMode) return;
    longPressOriginRef.current = {
      x: e.clientX ?? 0,
      y: e.clientY ?? 0,
    };
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = 'fired';
      onItemInfo(item);
    }, 500);
  }, [onItemInfo, editMode]);

  const handleLongPressMove = useCallback((e) => {
    if (!longPressTimer.current || longPressTimer.current === 'fired') return;
    const dx = Math.abs((e.clientX ?? 0) - longPressOriginRef.current.x);
    const dy = Math.abs((e.clientY ?? 0) - longPressOriginRef.current.y);
    if (dx > 8 || dy > 8) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer.current && longPressTimer.current !== 'fired') {
      clearTimeout(longPressTimer.current);
    }
    // Defer wipe so the upcoming click handler still sees the 'fired' marker
    if (longPressTimer.current === 'fired') {
      setTimeout(() => { longPressTimer.current = null; }, 50);
    } else {
      longPressTimer.current = null;
    }
  }, []);

  // Build categories
  const rawCategories = [...new Set(menuItems.map((item) => item.category))];
  const categories = bar ? barTabsFor(rawCategories, byMenuOrder(sectionPos)) : tabsFor(rawCategories, byMenuOrder(sectionPos));
  // The tab picked is kept in sight: the Bar opens on Drinks, at the far end of a long strip,
  // and nothing in view looked picked.
  const tabStrip = useRef(null);
  useEffect(() => {
    const el = tabStrip.current?.querySelector('[data-state="active"]');
    if (!el) return;
    const strip = tabStrip.current;
    const left = el.offsetLeft - strip.offsetLeft;
    if (left < strip.scrollLeft || left + el.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ left: Math.max(0, left + el.offsetWidth - strip.clientWidth + 8) });
    }
  }, [selectedCategory, categories.length]);

  // What each dish contains (a meal: anything its options could), and what of that this
  // table can't have. Off by default, the switch hides the clashing dishes altogether; on by
  // default it would hide the very dishes a server needs to explain.
  const allergyOf = useMemo(() => {
    const byId = Object.fromEntries((allMenuItems || []).map((m) => [m.id, m]));
    return Object.fromEntries((allMenuItems || []).map((m) => {
      const words = dishWords(m, byId);
      const contains = canonical(words);
      // `check`: words on the dish the till can't read against this table's allergy
      // ("butter", "nuts (almond)"). Not a clash it can name, and not a dish it can leave
      // unmarked either, so the card says to check. Only at a table with an allergy.
      return [m.id, {
        words,
        contains,
        clash: clash(contains, tableAllergens),
        check: tableAllergens.length ? unread(words) : [],
      }];
    }));
  }, [allMenuItems, tableAllergens]);
  const [hideClashing, setHideClashing] = useState(false);
  const tableHasAllergens = tableAllergens.length > 0;

  // Filter and sort menu items - memoize to prevent infinite loops
  const filteredMenu = useMemo(() => {
    const sub = selectedCategory === 'drinks' ? selectedDrinkSubCategory
      : selectedCategory === 'kids' ? selectedKidsSubCategory : null;
    return menuItems.filter((item) => (bar ? inBarTab(item.category, selectedCategory) : inTab(item.category, selectedCategory, sub)) && item.is_available
      && !(hideClashing && tableHasAllergens && allergyOf[item.id]?.clash.length));
  }, [menuItems, bar, selectedCategory, selectedDrinkSubCategory, selectedKidsSubCategory, hideClashing, tableHasAllergens, allergyOf]);

  // Apply custom order or default order (no alphabetical sort). Promoted items
  // are always pinned to the top of their category on the POS/Bar surfaces.
  useEffect(() => {
    const sorted = [...filteredMenu].sort((a, b) => {
      const pa = a.promoted ? 0 : 1;
      const pb = b.promoted ? 0 : 1;
      if (pa !== pb) return pa - pb;
      const orderA = customOrder[a.id] ?? 9999;
      const orderB = customOrder[b.id] ?? 9999;
      if (orderA !== orderB) return orderA - orderB;
      // Fallback to original database order (by ID or name as last resort)
      return 0;
    });
    setOrderedItems(sorted);
  }, [filteredMenu, customOrder]);

  // Drag and drop (Oct 2026 sweep): the dish you're holding leaves a gap where it would
  // land and the others slide along to make room, so it reads as moving the dish rather than
  // dropping it onto another one (which the old highlighted target looked like).
  const handleDragStart = (e, item, index) => {
    setDraggedItem({ item, index });
    setDragOverIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) setDragOverIndex(index);
  };

  // Leaving one card for the next fires leave then over; the gap stays where it last was.
  const handleDragLeave = () => {};

  // The tab's dishes in the order they'd be in if the held dish were dropped now.
  const previewItems = useMemo(() => {
    if (!draggedItem || dragOverIndex == null || dragOverIndex === draggedItem.index) return orderedItems;
    const next = [...orderedItems];
    const [held] = next.splice(draggedItem.index, 1);
    next.splice(dragOverIndex, 0, held);
    return next;
  }, [orderedItems, draggedItem, dragOverIndex]);

  const handleDrop = (e) => {
    e.preventDefault();
    if (!draggedItem) return;
    const newItems = previewItems;
    setDraggedItem(null);
    setDragOverIndex(null);
    if (newItems === orderedItems) return;
    setOrderedItems(newItems);
    // The whole menu's order, not just this tab's: the tab's dishes take their new order in
    // the places they already held, so moving a main doesn't send every pudding to the end.
    // From the whole saved order, not just the dishes this pad shows (lib/padOrder.js).
    const menuIds = [...menuItems].sort((a, b) => (customOrder[a.id] ?? 9999) - (customOrder[b.id] ?? 9999)).map((m) => m.id);
    setCustomOrder(toMap(mergeTabOrder(customOrder, menuIds, newItems.map((m) => m.id))));
    setHasUnsavedChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // Save the order to the person, on the venue's till server.
  const saveCustomOrder = async () => {
    const orderArray = Object.entries(customOrder).sort((a, b) => a[1] - b[1]).map(([id]) => id);
    try {
      await padApi.saveMenuOrder(orderArray);
      savedOrder.current = customOrder;
      toast.success('Your menu order is saved. It follows you to any till here.');
      setHasUnsavedChanges(false);
      setEditMode(false);
    } catch {
      toast.error("Couldn't save your menu order. Check the till is connected and try again.");
    }
  };

  // Back to the menu's own order, for this person.
  const resetCustomOrder = async () => {
    try {
      await padApi.saveMenuOrder([]);
      keepOrder({});
      setHasUnsavedChanges(false);
      setEditMode(false);
      toast.success("Back to the menu's own order");
    } catch {
      toast.error("Couldn't reset the order. Try again.");
    }
  };

  // Cancel edit mode
  const cancelEditMode = () => {
    setEditMode(false);
    setHasUnsavedChanges(false);
    setCustomOrder(savedOrder.current);
  };

  // Sections for All items, in the menu's order; each item keeps its index in `orderedItems`.
  const grouped = useMemo(() => {
    if (selectedCategory !== 'all' || editMode) return null;
    const by = new Map();
    orderedItems.forEach((item, index) => {
      if (!by.has(item.category)) by.set(item.category, []);
      by.get(item.category).push({ item, index });
    });
    return [...by.keys()].sort(byMenuOrder(sectionPos)).map((category) => ({ category, items: by.get(category) }));
  }, [orderedItems, selectedCategory, editMode, sectionPos]);

  // One dish card. `index` is its place in `orderedItems`, which the drag handlers use.
  // `index` is the card's place in the grid as shown (where a held dish would land);
  // `savedIndex` its place in the saved order, which picking it up starts from.
  const renderCard = (item, index, savedIndex = index) => (
        <Card
          key={item.id}
          className={`bg-white shadow-sm cursor-pointer hover:shadow-md transition-all select-none border border-hairline hover:border-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo ${
            editMode ? 'cursor-grab active:cursor-grabbing' : ''
          } ${
            draggedItem?.item.id === item.id ? '!border-dashed !border-verlo-hover bg-verlo-tint opacity-60' : ''
          } ${editMode ? 'transition-transform duration-200' : ''} ${
            // Off right now: muted and plainly not tappable. Holding it still opens the
            // sheet, for the allergens.
            item.available_now === false ? '!cursor-not-allowed bg-surface-2 opacity-60 hover:!shadow-sm hover:!border-hairline' : ''
          } ${
            allergyOf[item.id]?.clash.length || allergyOf[item.id]?.check.length ? 'border-signal-stop-line border-l-[3px] border-l-signal-stop-line' : ''
          }`}
          data-clash={allergyOf[item.id]?.clash.length ? '1' : undefined}
          data-off={item.available_now === false ? '1' : undefined}
          aria-disabled={item.available_now === false || undefined}
          // A control for the keyboard and switch access too (Chris's review M10): Enter or
          // Space adds it, as a tap does; the long press stays the touch way to the sheet.
          role="button"
          tabIndex={editMode ? -1 : 0}
          onKeyDown={(e) => {
            if (editMode || (e.key !== 'Enter' && e.key !== ' ')) return;
            e.preventDefault();
            onItemClick(item);
          }}
          onClick={() => {
            if (editMode) return;
            // Suppress click if long press just fired
            if (longPressTimer.current === 'fired') {
              longPressTimer.current = null;
              return;
            }
            onItemClick(item);
          }}
          draggable={editMode}
          onDragStart={(e) => editMode && handleDragStart(e, item, savedIndex)}
          onDragOver={(e) => editMode && handleDragOver(e, index)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => editMode && handleDrop(e)}
          onDragEnd={handleDragEnd}
          onPointerDown={(e) => !editMode && handleLongPressStart(item, e)}
          onPointerMove={handleLongPressMove}
          onPointerUp={handleLongPressEnd}
          onPointerCancel={handleLongPressEnd}
          onPointerLeave={handleLongPressEnd}
          onContextMenu={(e) => e.preventDefault()}
          style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
          data-testid={`menu-item-${item.name.toLowerCase().replace(/\s/g, '-')}`}
        >
          <CardContent className="flex h-full min-h-[92px] flex-col justify-between p-3">
            {/* The name is the card (FOH UX review, wave 2): it wraps to two lines and the
                description — never read at a till — is gone. Dietary marks are a suffix
                on the name line, small and quiet; allergens stay a signal. */}
            <div className="flex items-start gap-1">
              {editMode && (
                <GripVertical size={14} className="mt-0.5 shrink-0 text-ink-faint" />
              )}
              <h3 className={`font-heading text-[14px] font-bold leading-tight line-clamp-2 ${item.available_now === false ? 'text-ink-faint' : 'text-ink'}`}>
                {item.name}
                {(item.is_vegan || item.is_vegetarian || item.is_gluten_free) && (
                  <span className="ml-1.5 font-body text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                    {[item.is_vegan ? 'vg' : item.is_vegetarian ? 'v' : null, item.is_gluten_free ? 'gf' : null].filter(Boolean).join(' · ')}
                  </span>
                )}
              </h3>
            </div>
            <div className="mt-2 flex items-end justify-between gap-2">
              <span className={`font-mono text-[15px] font-medium tabular-nums ${item.available_now === false ? 'text-ink-faint' : 'text-verlo'}`}>
                {item.has_sizes && item.size_options?.length > 1
                  ? `from ${formatCurrency(item.price)}`
                  : formatCurrency(item.price)}
              </span>
              {/* When a dish is off, its hours replace the allergen count: a server
                  reaching for it needs to know when it is back, and the allergens of
                  something nobody can order are not the question (hours plan). */}
              {item.available_now === false ? (
                <Signal tone="warn" dot={false} data-testid="item-hours">{item.hours || 'off'}</Signal>
              ) : allergyOf[item.id]?.clash.length ? (
                <Signal tone="stop" dot={false} data-testid="item-clash">Contains {listWords(allergyOf[item.id].clash)}</Signal>
              ) : allergyOf[item.id]?.check.length ? (
                <Signal tone="stop" dot={false} data-testid="item-check">Check: {allergyOf[item.id].check.join(', ')}</Signal>
              ) : allergyOf[item.id]?.words.length ? (
                <span className="text-right text-[11px] leading-tight text-ink-faint line-clamp-2" data-testid="item-allergens">
                  {describe(allergyOf[item.id].words)}
                </span>
              ) : null}
            </div>
          </CardContent>
        </Card>
  );

  return (
    <div className={fill ? 'flex flex-col gap-2 lg:min-h-0 lg:flex-1' : 'space-y-2'}>
      {/* Category Tabs with Edit Mode Toggle. The table's allergy is on the order page's
          header, and every dish with it is marked in the grid; the switch to hide those dishes
          sits at the end of the tabs, so the allergy no longer costs the menu a row. */}
      <div className="shrink-0 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Tabs value={selectedCategory} onValueChange={(val) => {
            setSelectedCategory(val);
            setSelectedDrinkSubCategory(null);
            setSelectedKidsSubCategory(null);
          }} className="min-w-0 flex-1">
            {/* The first tab (All items, All drinks) is pinned to the left of the strip. A long
                menu scrolls the strip sideways, and picking Soft Drinks scrolled All Drinks out
                of sight, which read as the tab disappearing. */}
            <TabsList ref={tabStrip} className="no-scrollbar h-auto w-full justify-start gap-1 overflow-x-auto p-1 bg-paper">
              {categories.map((cat, i) => (
                <TabsTrigger
                  key={cat}
                  value={cat}
                  className={`h-11 shrink-0 px-3.5 text-[14px] data-[state=active]:bg-white data-[state=active]:shadow-sm ${
                    i === 0 ? 'sticky -left-1 z-10 bg-paper' : ''}`}
                  data-testid={`category-${cat}`}
                >
                  {bar && cat === 'drinks' ? 'All drinks' : cat === OTHER ? 'Everything else' : prettyCategory(cat)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {tableHasAllergens && (
            <label
              className="flex shrink-0 cursor-pointer items-center gap-2 text-[13px] text-ink-soft"
              title={`Hide dishes with ${listWords(tableAllergens)}`}
              data-testid="hide-clashing-label"
            >
              <span className="hidden xl:inline">Hide clashes</span><span className="xl:hidden">Hide</span>
              <Switch checked={hideClashing} onCheckedChange={setHideClashing} data-testid="hide-clashing" />
            </label>
          )}

          {/* Edit Mode Controls */}
          {showEditMode && (
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  {Object.keys(customOrder).length > 0 && (
                    <Button variant="ghost" size="sm" onClick={resetCustomOrder} className="text-ink-soft" data-testid="reset-menu-order-btn">
                      Reset
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelEditMode}
                    className="gap-1"
                  >
                    <X size={14} />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveCustomOrder}
                    disabled={!hasUnsavedChanges}
                    className="gap-1 bg-verlo hover:bg-verlo-hover"
                    data-testid="save-menu-order-btn"
                  >
                    <Check size={14} />
                    Save order
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditMode(true)}
                  className="h-9 w-9 shrink-0 text-ink-faint hover:text-ink"
                  data-testid="customize-menu-btn"
                  title="Customise: drag to reorder the menu for yourself"
                >
                  <Settings size={16} />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Edit Mode Banner */}
        {editMode && (
          <Notice tone="info" word="Editing">Drag items to reorder. The order is saved to your profile only.</Notice>
        )}

        {/* Drink Sub-categories */}
        {!bar && selectedCategory === 'drinks' && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedDrinkSubCategory === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedDrinkSubCategory(null)}
              className={selectedDrinkSubCategory === null ? 'bg-verlo' : ''}
            >
              All Drinks
            </Button>
            {drinkSubsOf(rawCategories).sort(byMenuOrder(sectionPos)).map((subCat) => (
              <Button
                key={subCat}
                variant={selectedDrinkSubCategory === subCat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedDrinkSubCategory(subCat)}
                className={selectedDrinkSubCategory === subCat ? 'bg-verlo' : ''}
              >
                {prettyCategory(subCat)}
              </Button>
            ))}
          </div>
        )}

        {/* Kids Sub-categories */}
        {selectedCategory === 'kids' && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedKidsSubCategory === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedKidsSubCategory(null)}
              className={selectedKidsSubCategory === null ? 'bg-verlo' : ''}
            >
              All Kids
            </Button>
            {kidsSubCategories.filter((c) => rawCategories.includes(c)).sort(byMenuOrder(sectionPos)).map((subCat) => (
              <Button
                key={subCat}
                variant={selectedKidsSubCategory === subCat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedKidsSubCategory(subCat)}
                className={selectedKidsSubCategory === subCat ? 'bg-verlo' : ''}
              >
                {categoryDisplayNames[subCat]}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Menu Grid (no A-Z sidebar) */}
      <ScrollArea className={fill ? 'h-[calc(100vh-24rem)] lg:h-auto lg:min-h-0 lg:flex-1' : 'h-[calc(100vh-24rem)]'} ref={menuScrollRef}>
        {/* All items reads the way the menu does: each section under its own heading, in the
            menu's order, rather than one alphabet where Aperol Spritz sits beside BBQ Bacon
            Stack. Arranging (edit mode) and a single section stay one flat grid. */}
        {grouped ? (
          <div className="space-y-4 pr-4">
            {grouped.map((g) => (
              <section key={g.category} data-testid={`menu-group-${g.category}`}>
                <div className="mb-2 flex items-center gap-2.5">
                  <h3 className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink">{groupLabel(g.category)}</h3>
                  <span className="h-px flex-1 bg-hairline-strong" />
                  <span className="font-mono text-[11px] text-ink-faint">{g.items.length}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {g.items.map(({ item, index }) => renderCard(item, index))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pr-4">
            {/* While a dish is held, the grid shows where everything would go. Each card keeps
                its index in the saved order, which is what the drop handlers work from. */}
            {previewItems.map((item) => {
              const index = orderedItems.indexOf(item);
              return renderCard(item, previewItems.indexOf(item), index);
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default MenuSection;
